// Client-safe config keys. Safe to import in browser bundles, React components, and server code.
// @see config-keys-server.ts for server-only keys
import { Config, ConfigProvider, Effect, Option, Schema } from 'effect';
import {
  fallbackMode,
  getStageFromEnv,
  modeConfig,
  modeFromStage,
  type Mode,
  type Stage,
} from '@listening/env/mode';
import {
  withComputedDefault,
  withModeDefault,
} from '@listening/env/config-util';
import { defineConfigKey, envConfigProvider } from '@listening/env/config-key';

export { modeConfig } from '@listening/env/mode';

// Fixed UUIDs for the 6 default/system audio conversions in Postgres.
export const DEFAULT_AUDIO_IDS = [
  '00000000-0000-0000-0000-000000000001',
  '00000000-0000-0000-0000-000000000002',
  '00000000-0000-0000-0000-000000000003',
  '00000000-0000-0000-0000-000000000004',
  '00000000-0000-0000-0000-000000000005',
  '00000000-0000-0000-0000-000000000006',
];

// Default ports for local dev services. Single source of truth — import these
// instead of hardcoding port numbers in app configs, docker health checks, etc.
export const DEFAULT_PORTS = {
  api: 3007,
  clusterWorker: 3105,
  forceAligner: 3106,
} as const;

// The port each service binds. These live here rather than in the app that
// serves them because the api's e2e setup and the mobile e2e script both spawn
// those services and need to reach them — 2+ consumers across package
// boundaries is the tier rule, and it is decided by the import graph rather
// than by which app the key sounds like it belongs to.
// @see docs/projects/complete/env-config-refactor-v5.md (Phase 3)
export const clusterWorkerPort = Config.Port('PORT').pipe(
  Config.withDefault(DEFAULT_PORTS.clusterWorker),
);

export const forceAlignerPort = Config.Port('PORT').pipe(
  Config.withDefault(DEFAULT_PORTS.forceAligner),
);

export const stageConfig = defineConfigKey('STAGE', (name) =>
  Config.String(name).pipe(
    Config.withDefault(getStageFromEnv() ?? fallbackMode),
  ),
);

export const ciConfig = Config.Boolean('CI').pipe(Config.withDefault(false));

export const siteUrlConfig = Config.String('VITE_SITE_URL').pipe(
  withComputedDefault(
    { mode: modeConfig, stage: stageConfig },
    ({ mode, stage }) => {
      // Preview stages (pr-123) get their own subdomain under dev.listening.com
      if (mode === 'development' && stage.startsWith('pr-'))
        return `https://${stage}.dev.listening.com`;
      return (
        {
          localstack: `http://127.0.0.1:${DEFAULT_PORTS.api}`,
          development: 'https://dev.listening.com',
          staging: 'https://staging.listening.com',
          production: 'https://www.listening.com',
        } satisfies Record<Mode, string>
      )[mode];
    },
  ),
);

/**
 * Custom URL schemes the mobile builds register. Two of them because a shared
 * scheme leaves iOS free to pick either app when both are installed, so the
 * dev build claims its own — see `apps/mobile/app.config.ts`.
 *
 * `listening` is prod (also the scheme the shipped Flutter app answers to);
 * `listening-dev` is the dev/staging variant.
 */
export const mobileDeepLinkSchemes = ['listening', 'listening-dev'] as const;
export type MobileDeepLinkScheme = (typeof mobileDeepLinkSchemes)[number];

/**
 * The scheme THIS backend hands to mobile clients (OAuth callback deep links)
 * when the app did not name its own. Derived from mode so it matches the app
 * variant that normally talks to this ring: only the production ring serves the
 * prod build.
 *
 * The one combination this default gets "wrong" is a dev build pointed at the
 * production ring via the in-app ring switcher — that build registers
 * `listening-dev` but production emits `listening`. Google Drive sends the
 * scheme it registers when it starts a mobile connection, so its callback
 * returns to the right build on any ring; Dropbox and Zotero still rely on this
 * default and the app disables them on that combination (see
 * apps/mobile/src/lib/config/backend-ring-core.ts).
 */
export const mobileDeepLinkSchemeConfig = Config.schema(
  Schema.Literals(mobileDeepLinkSchemes),
  'MOBILE_DEEP_LINK_SCHEME',
).pipe(
  withComputedDefault({ mode: modeConfig }, ({ mode }): MobileDeepLinkScheme =>
    mode === 'production' ? 'listening' : 'listening-dev',
  ),
);

export const backendUrlConfig = Config.String('VITE_BACKEND_URL').pipe(
  withComputedDefault(
    { siteUrl: siteUrlConfig },
    ({ siteUrl }) => `${siteUrl}/api`,
  ),
);

export const chromeStoreUrlConfig = Config.String('VITE_CHROME_STORE_URL').pipe(
  Config.withDefault(
    'https://chromewebstore.google.com/detail/listeningcom/amjjglmomeodglgclcnjdjbhhcfnbnpd',
  ),
);

export const posthogHostConfig = Config.String('VITE_PUBLIC_POSTHOG_HOST').pipe(
  Config.withDefault('https://posthog.listening.io'),
);

export const posthogPublicKeyConfig = Config.String(
  'VITE_PUBLIC_POSTHOG_KEY',
).pipe(Config.withDefault('phc_7All58kB0kBpKKpzzB8Fy9ctvXGaw6i82rTSaPXy7k9'));

/**
 * Path prefix of the same-origin PostHog ingest proxy — a CloudFront behavior
 * on the start app's distribution that strips this prefix and forwards to
 * `posthogHost`. ONE source of truth: the start app's CDN declaration in
 * packages/infra reads it to build the behavior's path pattern and the
 * prefix-stripping edge function, and `posthogIngestHostConfig` below builds
 * the browser's `api_host` from it. A literal on either side would drift and
 * silently 404 every event.
 *
 * The value is deliberately meaningless, because filter lists block by path as
 * well as by host. uBlock Origin's default privacy list carries
 * `/ingest/*^ip=`, `/ingress/*^ip=` and `/hog/*^ip=` under a `! posthog`
 * heading — so PostHog's own documented `/ingest` is already a dead path, as
 * is anything containing `posthog`, `ph`, or `analytics`. Assume this prefix
 * gets catalogued eventually too; rotating it is a change to this default plus
 * a start-app deploy.
 */
export const posthogIngestPathConfig = Config.String(
  'VITE_PUBLIC_POSTHOG_INGEST_PATH',
).pipe(Config.withDefault('/lsn'));

/**
 * Browser-facing PostHog origin — what `posthog-js` gets as `api_host`.
 *
 * Same-origin on every deployed stage: ad blockers see a first-party request
 * instead of `posthog.listening.io`. EasyPrivacy's `://posthog.$script` rule
 * matches any host beginning `posthog.`, so for uBlock Origin users the
 * browser refuses to fetch `/array/<token>/config.js` and
 * `/static/<ver>/*.js` — the origin serves them fine, the request never
 * leaves. Same-origin also means no cross-origin preflight, which is the
 * reason the CORS allow-list in packages/infra/src/ovh/boxes/posthog exists at all.
 *
 * Localstack has no CloudFront in front of it, so it keeps talking to
 * `posthogHost` directly (and stays covered by that CORS allow-list).
 *
 * Server-side capture keeps using `posthogHost`: nothing blocks a server, and
 * routing it through the CDN would only add a hop.
 */
export const posthogIngestHostConfig = Config.String(
  'VITE_PUBLIC_POSTHOG_INGEST_HOST',
).pipe(
  withComputedDefault(
    {
      mode: modeConfig,
      siteUrl: siteUrlConfig,
      posthogHost: posthogHostConfig,
      ingestPath: posthogIngestPathConfig,
    },
    ({ mode, siteUrl, posthogHost, ingestPath }) =>
      mode === 'localstack' ? posthogHost : `${siteUrl}${ingestPath}`,
  ),
);

// Public sitekey for the Cloudflare Turnstile widget on the public-tools
// demo submit endpoints. The widget is per-stage (provisioned in
// packages/infra/src/cloudflare.ts, `mode: 'invisible'`). Committed per-mode
// defaults are the source of truth for permanent stages; env var override
// is used for PR previews (each gets its own widget). Localstack uses
// Cloudflare's invisible-mode "always passes" test key so local dev and
// Playwright work without a real widget — pair it with the matching test
// secret `1x0000000000000000000000000000000AA` on the API.
// @see https://developers.cloudflare.com/turnstile/troubleshooting/testing/
export const turnstileSiteKeyConfig = defineConfigKey(
  'VITE_TURNSTILE_SITE_KEY',
  (name) =>
    withModeDefault(Config.String(name), {
      default: '1x00000000000000000000BB',
      development: '0x4AAAAAADSf3mVf-pPK_0T4',
      staging: '0x4AAAAAADWvnrbxZdjyrdeM',
      production: '0x4AAAAAADXVyVmMEhq7XSBu',
    }),
);

/**
 * Env vars that carry IaC-provisioned PUBLIC values into the Vite browser
 * bundle. One name here used to mean five hand-wired touchpoints; now four:
 *   1+2. the site's build env + Lambda env — derived from
 *        the manifest in packages/infra/src/web/public-bundle-env.ts
 *   3.   apps/start/turbo.jsonc `env` + `passThroughEnv` — can't be generated,
 *        enforced by apps/start/test/unit/public-bundle-env.spec.ts
 *   4.   Vite inlines `import.meta.env.<NAME>` into the bundle
 * Adding a key: defineConfigKey it, add its envVarName here, add the value to
 * makePublicBundleEnv, and add both turbo.jsonc entries (the test will remind you).
 */
export const PUBLIC_BUNDLE_ENV_VAR_NAMES = [
  turnstileSiteKeyConfig.envVarName,
] as const;

// Must match the Firebase project's Web OAuth Client ID exactly so OAuth account IDs are portable
export const googleOauthClientIdConfig = withModeDefault(
  Config.String('GOOGLE_OAUTH_CLIENT_ID'),
  {
    default:
      '291677000498-rfh027qb77rohnbclr1gf0iesni3k9v2.apps.googleusercontent.com',
    production:
      '327902788428-hoeoeaadjc283noqspga29an2a1nfbf1.apps.googleusercontent.com',
  },
);

// Mobile Google OAuth client IDs — ID tokens from native Google Sign-In have these as `aud`.
// The server must accept all three (web + iOS + Android) when verifying mobile ID tokens.
export const googleOauthIosClientIdConfig = withModeDefault(
  Config.String('GOOGLE_OAUTH_IOS_CLIENT_ID'),
  {
    default:
      '291677000498-9l1a9lesp3smh0bj50lfqoqlemd3v58j.apps.googleusercontent.com',
    production:
      '327902788428-d1hc19rgl0s3hoj9submkpusmes546jq.apps.googleusercontent.com',
  },
);

export const googleOauthAndroidClientIdConfig = withModeDefault(
  Config.String('GOOGLE_OAUTH_ANDROID_CLIENT_ID'),
  {
    default:
      '291677000498-0q829mvinfmj80vnnceoelqoape09lid.apps.googleusercontent.com',
    production:
      '327902788428-coq94t6grvs83j569lkis044gnktfjvm.apps.googleusercontent.com',
  },
);

// Apple Service ID for Sign in with Apple (web). The mobile app uses the bundle ID directly.
export const appleOauthClientIdConfig = withModeDefault(
  Config.String('APPLE_OAUTH_CLIENT_ID'),
  {
    default: 'com.listening.signin.dev',
    production: 'com.listening.signin',
  },
);

// iOS bundle ID — Apple ID tokens from native Sign in with Apple have this as `aud`.
export const appleOauthIosBundleIdConfig = withModeDefault(
  Config.String('APPLE_OAUTH_IOS_BUNDLE_ID'),
  {
    default: 'io.listening.main.dev',
    production: 'io.listening.main',
  },
);

export const defaultAudioIdsConfig = Config.Array(
  Schema.String,
  'DEFAULT_AUDIO_CONVERSION_IDS_NEW',
).pipe(Config.withDefault(DEFAULT_AUDIO_IDS));

export const SPANISH_WELCOME_AUDIO_ID_DEFAULT =
  '00000000-0000-0000-0000-000000000007';

export const spanishWelcomeAudioIdConfig = Config.String(
  'SPANISH_WELCOME_AUDIO_ID',
).pipe(Config.withDefault(SPANISH_WELCOME_AUDIO_ID_DEFAULT));

// The English welcome is the last entry in DEFAULT_AUDIO_CONVERSION_IDS_NEW.
export const welcomeAudioIdByLanguageConfig = Config.all({
  defaultAudioIds: defaultAudioIdsConfig,
  spanishWelcomeAudioId: spanishWelcomeAudioIdConfig,
}).pipe(
  Config.map(({ defaultAudioIds, spanishWelcomeAudioId }) => {
    const englishWelcomeAudioId = defaultAudioIds.at(-1);
    return {
      ...(englishWelcomeAudioId !== undefined && { en: englishWelcomeAudioId }),
      es: spanishWelcomeAudioId,
    };
  }),
);

export const allDefaultAudioIdsConfig = Config.all({
  defaultAudioIds: defaultAudioIdsConfig,
  welcomeAudioIdByLanguage: welcomeAudioIdByLanguageConfig,
}).pipe(
  Config.map(({ defaultAudioIds, welcomeAudioIdByLanguage }) => [
    ...new Set([
      ...defaultAudioIds,
      ...Object.values(welcomeAudioIdByLanguage).filter(
        (id): id is string => !!id,
      ),
    ]),
  ]),
);

// ---------------------------------------------------------------------------
// Stripe keys
// ---------------------------------------------------------------------------

export const stripePublicKeyConfig = withModeDefault(
  Config.String('VITE_STRIPE_PUBLIC_KEY'),
  {
    default:
      'pk_test_51TyxYPRTADZl0cwJseLD4EsmfKc5Gxg1ZD8jxrt5mr13hvrmmwx6xFq6j3t4WeZCLITB0U51ekTE6CCzqY4nqzgv00ibxiwsOK',
    staging:
      'pk_test_51TyxZJRCfhg0LQO9ul6bRkCft14lxKGR3cSWPHOxYTFIc26Nz6wVQwMSxpCLc7idTlFghDcaA6oskifyhlJ5t1qH00eIXducAK',
    production: 'pk_live_PHxtasoValiDfGsVkq9OvOoO',
  },
);

export const stripeYearly125IdConfig = withModeDefault(
  Config.String('VITE_STRIPE_YEARLY_125_ID'),
  {
    default: 'price_1P8mR2Eg38EhCME3Zv4mkbf5',
    production: 'price_1P8mSbEg38EhCME3rfVcu5GM',
  },
);

export const stripeYearly99IdConfig = withModeDefault(
  Config.String('VITE_STRIPE_YEARLY_99_ID'),
  {
    default: 'price_1NijfoEg38EhCME31QaisFhg',
    production: 'price_1NiidfEg38EhCME3YE59TtID',
  },
);

export const stripeYearly69IdConfig = withModeDefault(
  Config.String('VITE_STRIPE_YEARLY_69_ID'),
  {
    default: 'price_1Ppz5oEg38EhCME3u77ZcyvH',
    production: 'price_1Ppz7GEg38EhCME3mwVU5bgR',
  },
);

export const stripeYearly59IdConfig = withModeDefault(
  Config.String('VITE_STRIPE_YEARLY_59_ID'),
  {
    default: 'price_1QNRHiEg38EhCME3ADIatGKY',
    production: 'price_1QNRIVEg38EhCME30VFJHQ5P',
  },
);

export const stripeYearly39IdConfig = withModeDefault(
  Config.String('VITE_STRIPE_YEARLY_39_ID'),
  {
    default: 'price_1R7hs4Eg38EhCME3tfUmWy4z',
    production: 'price_1R7iqbEg38EhCME3tN98bLRP',
  },
);

// pricing-test-v1's $79 annual tier. Both ids sit on the same product as the
// other annual tiers in their respective modes.
export const stripeYearly79IdConfig = withModeDefault(
  Config.String('VITE_STRIPE_YEARLY_79_ID'),
  {
    default: 'price_1U6a6NEg38EhCME3bkbYGa0K',
    production: 'price_1OFLrXEg38EhCME3PMrDZ5zq',
  },
);

export const stripeMonthly19IdConfig = withModeDefault(
  Config.String('VITE_STRIPE_MONTHLY_19_ID'),
  {
    default: 'price_1PEqK7Eg38EhCME3r6QvLhFk',
    production: 'price_1PEqKYEg38EhCME3zht0aCKD',
  },
);

export const stripeMonthly12IdConfig = withModeDefault(
  Config.String('VITE_STRIPE_MONTHLY_12_ID'),
  {
    default: 'price_1MUhRuEg38EhCME38m30sVTn',
    production: 'price_1MUhLbEg38EhCME3TrVs6ZDb',
  },
);

export const stripeMonthly1299IdConfig = withModeDefault(
  Config.String('VITE_STRIPE_MONTHLY_12_99_ID'),
  {
    default: 'price_1MHvzEEg38EhCME3bTjh1HqL',
    production: 'price_1MRMflEg38EhCME3QGeN2q8w',
  },
);

// ---------------------------------------------------------------------------
// Behavioral keys (shared across apps)
// ---------------------------------------------------------------------------

export const devToolsConfig = withModeDefault(Config.Boolean('DEV_TOOLS'), {
  default: false,
  localstack: true,
});

export const conversionModeConfig = withModeDefault(
  Config.schema(Schema.Literals(['local', 'remote']), 'CONVERSION_MODE'),
  { default: 'remote' as const, localstack: 'local' as const },
);

// ---------------------------------------------------------------------------
// Keys moved from app-specific config files
// ---------------------------------------------------------------------------

export const sentryDsnConfig = Config.String('VITE_SENTRY_DSN').pipe(
  Config.withDefault(
    'https://b7e927dea0aadaf858b212ed9142103d@sentry-ovh.listening.io/2',
  ),
);

export const wordpressGraphqlUrlConfig = Config.String(
  'VITE_WORDPRESS_GRAPHQL_URL',
).pipe(Config.withDefault('https://listeningblog.wpenginepowered.com/graphql'));

export const profitwellAuthKeyConfig = Config.String(
  'VITE_PROFITWELL_AUTH_KEY',
).pipe(Config.withDefault('477fc353ee557db93e9f64bc5547c3fb'));

export const podcastPagesEnabledConfig = Config.Boolean(
  'VITE_PODCAST_PAGES_ENABLED',
).pipe(Config.withDefault(false));

export const desktopDownloadsEnabledConfig = withModeDefault(
  Config.Boolean('VITE_DESKTOP_DOWNLOADS_ENABLED'),
  {
    default: false,
    development: true,
    production: true,
    staging: true,
  },
);

export const zoteroUiEnabledConfig = withModeDefault(
  Config.Boolean('VITE_ZOTERO_UI_ENABLED'),
  {
    default: true,
    development: true,
    staging: true,
  },
);

// Dev/staging-only web-app tooling for the v1/v2 conversion-pipeline swap: a settings toggle to
// pick the pipeline per-upload + pipeline/render-format badges. Off in production (unlisted modes
// fall through to `default`). @see docs/projects/active/conversion-pipeline-v2/web-app-tooling.md
export const conversionPipelineToggleEnabledConfig = withModeDefault(
  Config.Boolean('VITE_CONVERSION_PIPELINE_TOGGLE_ENABLED'),
  {
    default: false,
    development: true,
    staging: true,
  },
);

export const documentInsightsEnabledConfig = withModeDefault(
  Config.Boolean('VITE_DOCUMENT_INSIGHTS_ENABLED'),
  { default: true },
);

export const publicToolsEnabledConfig = withModeDefault(
  Config.Boolean('PUBLIC_TOOLS_ENABLED').pipe(
    Config.orElse(() => Config.Boolean('VITE_PUBLIC_TOOLS_ENABLED')),
  ),
  {
    default: true,
    production: true,
  },
);

export const publicToolDownloadsEnabledConfig = Config.Boolean(
  'PUBLIC_TOOL_DOWNLOADS_ENABLED',
).pipe(
  Config.orElse(() => Config.Boolean('VITE_PUBLIC_TOOL_DOWNLOADS_ENABLED')),
  Config.withDefault(false),
);

export const isTestConfig = Config.Boolean('IS_TEST').pipe(
  Config.withDefault(false),
);

// Set by the dev scripts, which only ever run on a laptop.
export const localDevConfig = Config.Boolean('VITE_LOCAL_DEV').pipe(
  Config.withDefault(false),
);

// Every stage shares one PostHog project (`posthogPublicKeyConfig` has no
// per-stage value), so only deployed staging and production may write to it —
// `--mode staging` on a dev server is for reaching the staging API, not for
// emitting events. Set VITE_ANALYTICS_ENABLED=true to opt in locally.
export const analyticsEnabledConfig = Config.Boolean(
  'VITE_ANALYTICS_ENABLED',
).pipe(
  withComputedDefault(
    { mode: modeConfig, localDev: localDevConfig },
    ({ mode, localDev }) =>
      !localDev && (mode === 'staging' || mode === 'production'),
  ),
);

export const clientEnvStringConfig = withModeDefault(
  Config.String('ENV_STRING'),
  {
    default: 'localstack',
    development: 'development',
    staging: 'staging',
    production: 'production',
  },
);

// ---------------------------------------------------------------------------
// Runtime detection — client-safe because they only read standard env vars
// (IS_LAMBDA, AWS_LAMBDA_FUNCTION_NAME, NODE_ENV, IS_PROD_BUILD).
// ---------------------------------------------------------------------------

export const nodeEnvConfig = Config.option(Config.String('NODE_ENV'));

export const isLambdaConfig = Config.Boolean('IS_LAMBDA').pipe(
  withComputedDefault(
    {
      awsLambdaFunctionName: Config.option(
        Config.String('AWS_LAMBDA_FUNCTION_NAME'),
      ),
    },
    ({ awsLambdaFunctionName }) => Option.isSome(awsLambdaFunctionName),
  ),
);

export const isProdBuildConfig = Config.Boolean('IS_PROD_BUILD').pipe(
  withComputedDefault(
    { nodeEnv: nodeEnvConfig },
    ({ nodeEnv }) => Option.getOrElse(nodeEnv, () => '') === 'production',
  ),
);

// ---------------------------------------------------------------------------
// Client-safe aggregation — use for module-scope access in React components.
// For server-only code, use unsafeLoadEnvFromStage() from @listening/env/repo-wide-config.
// ---------------------------------------------------------------------------

/**
 * Keys safe to bundle in client builds. Server-only keys (like secrets,
 * database URLs) live in repo-wide-config.ts and are spread over this map.
 */
export const clientEnvKeys = {
  mode: modeConfig,
  stage: stageConfig,
  ci: ciConfig,
  siteUrl: siteUrlConfig,
  backendUrl: backendUrlConfig,
  chromeStoreUrl: chromeStoreUrlConfig,
  posthogHost: posthogHostConfig,
  posthogIngestPath: posthogIngestPathConfig,
  posthogIngestHost: posthogIngestHostConfig,
  posthogPublicKey: posthogPublicKeyConfig,
  googleOauthClientId: googleOauthClientIdConfig,
  appleOauthClientId: appleOauthClientIdConfig,
  defaultAudioIds: defaultAudioIdsConfig,
  stripePublicKey: stripePublicKeyConfig,
  stripeYearly125Id: stripeYearly125IdConfig,
  stripeYearly99Id: stripeYearly99IdConfig,
  stripeYearly69Id: stripeYearly69IdConfig,
  stripeYearly59Id: stripeYearly59IdConfig,
  stripeYearly39Id: stripeYearly39IdConfig,
  stripeYearly79Id: stripeYearly79IdConfig,
  stripeMonthly19Id: stripeMonthly19IdConfig,
  stripeMonthly12Id: stripeMonthly12IdConfig,
  stripeMonthly1299Id: stripeMonthly1299IdConfig,
  devTools: devToolsConfig,
  conversionMode: conversionModeConfig,
  sentryDsn: sentryDsnConfig,
  wordpressGraphqlUrl: wordpressGraphqlUrlConfig,
  profitwellAuthKey: profitwellAuthKeyConfig,
  podcastPagesEnabled: podcastPagesEnabledConfig,
  desktopDownloadsEnabled: desktopDownloadsEnabledConfig,
  zoteroUiEnabled: zoteroUiEnabledConfig,
  conversionPipelineToggleEnabled: conversionPipelineToggleEnabledConfig,
  documentInsightsEnabled: documentInsightsEnabledConfig,
  publicToolsEnabled: publicToolsEnabledConfig,
  publicToolDownloadsEnabled: publicToolDownloadsEnabledConfig,
  turnstileSiteKey: turnstileSiteKeyConfig,
  isTest: isTestConfig,
  localDev: localDevConfig,
  analyticsEnabled: analyticsEnabledConfig,
  envString: clientEnvStringConfig,
  isLambda: isLambdaConfig,
  isProdBuild: isProdBuildConfig,
} as const;

export const unsafeLoadClientEnv = (argStage?: Stage) => {
  const provider = argStage
    ? ConfigProvider.fromUnknown({
        MODE: modeFromStage(argStage),
        STAGE: argStage,
      }).pipe(ConfigProvider.orElse(envConfigProvider()))
    : envConfigProvider();

  return Config.all(clientEnvKeys).pipe(
    Effect.provide(ConfigProvider.layer(provider)),
    Effect.runSync,
  );
};

export type ClientEnv = ReturnType<typeof unsafeLoadClientEnv>;
